// src/pages/CalculatorValuation.js
import React, { useState, useEffect } from "react";

const stageOptions = ["Pre-MVP", "MVP", "GTM", "Growth", "Maturity"];

// Default multipliers by stage (examples, adjust as needed)
const defaultMultipliers = {
  "Pre-MVP": { "Technology": 2, "Operations": 1, "R&D": 2 },
  "MVP": { "Salaries": 2, "Technology": 1.5, "Traction": 1.5 },
  "GTM": { "Marketing": 3, "Sales": 2, "Projected Revenue": 2 },
  "Growth": { "Subscribers": 2, "Projected Subscribers": 1.5, "Customer Success": 2 },
  "Maturity": { "Current Revenue": 2, "EBITDA": 8, "PE Ratio": 10 }
};

// Industry standard items by stage
const industryItems = {
  "Pre-MVP": ["R&D", "Prototype", "Founders"],
  "MVP": ["Salaries", "Technology", "Operations", "Traction"],
  "GTM": ["Marketing", "Sales", "Distribution", "Projected Revenue"],
  "Growth": ["Customer Success", "Infrastructure", "Subscribers", "Projected Subscribers"],
  "Maturity": ["Compliance", "Legal & Compliance", "Governance", "Current Revenue", "EBITDA", "PE Ratio"]
};

// Always-available valuation factors
const valuationFactors = [
  "Total Current Shares",
  "Share Value at Par",
  "Current Capital",
  "Paid-Up Capital",
  "Number of Shares",
  "ESOPs",
  "Owner Shares",
  "Cash Flow",
  "Fixed Assets Value",
  "Liquid Assets Value",
  "PE Ratio",
  "EBITDA",
  "Traction",
  "Projected Revenue",
  "Current Revenue",
  "Subscribers",
  "Projected Subscribers",
  "Other"
];

export default function CalculatorValuation({ 
  initialStage = "", 
  budgetItems = [], // [{item:"Salaries", value:500000}, ...]
  initialRows = [], 
  onSave, 
  onCancel 
}) {
  const [stage, setStage] = useState(initialStage);
  const [rows, setRows] = useState(initialRows);

  // Auto-populate rows when stage changes
  useEffect(() => {
    const items = [
      ...budgetItems.map(b => b.item),
      ...(industryItems[stage] || []),
      ...valuationFactors
    ];

    const newRows = items.map(item => {
      const budgetMatch = budgetItems.find(b => b.item === item);
      const baseValue = budgetMatch ? budgetMatch.value : 0;
      const multiplier = defaultMultipliers[stage]?.[item] || 1;
      return {
        item,
        baseValue,
        multiplier,
        driverValue: baseValue * multiplier,
        remark: ""
      };
    });

    setRows(newRows);
  }, [stage, budgetItems]);

  const handleRowChange = (index, field, value) => {
    const updated = [...rows];
    if (field === "multiplier") {
      updated[index].multiplier = Number(value);
      updated[index].driverValue = updated[index].baseValue * updated[index].multiplier;
    } else if (field === "item") {
      updated[index].item = value;
      const match = budgetItems.find(b => b.item === value);
      updated[index].baseValue = match ? match.value : 0;
      updated[index].driverValue = updated[index].baseValue * updated[index].multiplier;
    } else if (field === "baseValue") {
      updated[index].baseValue = Number(value);
      updated[index].driverValue = updated[index].baseValue * updated[index].multiplier;
    } else if (field === "remark") {
      updated[index].remark = value;
    }
    setRows(updated);
  };

  const addRow = () => {
    setRows([...rows, { item: "", baseValue: 0, multiplier: 1, driverValue: 0, remark: "" }]);
  };

  const removeRow = (index) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  const totalValuation = rows.reduce((sum, r) => sum + (r.driverValue || 0), 0);

  const handleSave = () => {
    if (typeof onSave === "function") {
      onSave({ stage, total: totalValuation, breakdown: rows });
    }
  };

  return (
    <div style={{
      position: "fixed", top: "50%", left: "50%",
      transform: "translate(-50%, -50%)",
      background: "#fff", border: "1px solid #ccc",
      borderRadius: "8px", width: "850px", maxHeight: "80vh",
      overflowY: "auto", zIndex: 10001, padding: "20px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
    }}>
      <h4>Valuation Calculator</h4>

      <label>Stage</label>
      <select
        value={stage}
        onChange={(e) => setStage(e.target.value)}
        className="w3-select w3-border"
        style={{ marginBottom: "15px" }}
      >
        <option value="">-- Select Stage --</option>
        {stageOptions.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>

      <table className="w3-table-all" style={{ marginTop: "10px" }}>
        <thead>
          <tr>
            <th>Item</th>
            <th>Base Value (₹)</th>
            <th>Multiplier</th>
            <th>Contribution (₹)</th>
            <th>Remark</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td>
                <select
                  value={row.item}
                  onChange={(e) => handleRowChange(i, "item", e.target.value)}
                >
                  <option value="">-- Select Item --</option>
                  {[...new Set([...budgetItems.map(b => b.item), ...(industryItems[stage] || []), ...valuationFactors])]
                    .map(item => <option key={item} value={item}>{item}</option>)}
                </select>
              </td>
              <td>
                <input
                  type="number"
                  value={row.baseValue}
                  onChange={(e) => handleRowChange(i, "baseValue", e.target.value)}
                />
              </td>
              <td>
                <input
                  type="number"
                  value={row.multiplier}
                  onChange={(e) => handleRowChange(i, "multiplier", e.target.value)}
                  style={{ width: "80px" }}
                />
              </td>
              <td>{row.driverValue.toLocaleString("en-IN")}</td>
              <td>
                <input
                  type="text"
                  value={row.remark}
                  onChange={(e) => handleRowChange(i, "remark", e.target.value)}
                  placeholder="Add remark"
                />
              </td>
              <td>
                <button className="w3-button w3-red" onClick={() => removeRow(i)}>✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button className="w3-button w3-light-grey" style={{ marginTop: "10px" }} onClick={addRow}>
        + Add Row
      </button>

      <div style={{ marginTop: "15px" }}>
        <strong>Total Valuation: ₹{totalValuation.toLocaleString("en-IN")}</strong>
      </div>

      <div style={{ marginTop: "15px" }}>
        <button className="w3-button w3-teal" onClick={handleSave}>Save</button>
        <button className="w3-button w3-light-grey" style={{ marginLeft: "10px" }} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
